import React, { useState, useEffect, useMemo, use } from 'react';
import {
  Table,
  Select,
  Button,
  Space,
  Tag,
  Card,
  DatePicker
} from 'antd';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filter Types Configuration
const FILTER_TYPES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  DATE: 'date'
};

// Helper function to get params from URL
const useUrlParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramsObj = {};

    for (const [key, value] of searchParams.entries()) {
      // Handle multiple values for the same key (arrays)
      if (paramsObj[key]) {
        if (Array.isArray(paramsObj[key])) {
          paramsObj[key].push(value);
        } else {
          paramsObj[key] = [paramsObj[key], value];
        }
      } else {
        paramsObj[key] = value;
      }
    }

    return paramsObj;
  }, [location.search]);

  const updateParams = (newParams) => {
    const searchParams = new URLSearchParams();

    // Add all params to URLSearchParams
    Object.entries(newParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v));
      } else if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });

    // Update URL without causing a page reload
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  return { params, updateParams };
};

// Main Table Component
const AdvancedTable = ({
  data = [],
  total = 0,
  columns = [],
  filterOptions = [],
  paginationOptions = {
    enabled: true,
    pageSize: 10,
    pageSizeOptions: ['10', '20', '50', '100'],
    showSizeChanger: true,
    showQuickJumper: true
  },
  loading = false,
  bordered = true,
  ctx
}) => {
  const { params, updateParams } = useUrlParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(paginationOptions.pageSize);
  // if (ctx?.state.access?.verificationAccess) {
  //   filterOptions.push({
  //     field: 'isVerified',
  //     label: 'Verified Status',
  //     type: 'single',
  //     options: [
  //       { value: '1', label: 'Verified' },
  //       { value: '0', label: 'Unverified' },
  //     ]
  //   });
  // }
  const paginationConfig = useMemo(() => {
    if (!paginationOptions.enabled) return false;

    return {
      current: currentPage,
      pageSize: pageSize,
      total: total, // Use the total prop directly here
      showSizeChanger: paginationOptions.showSizeChanger,
      showQuickJumper: paginationOptions.showQuickJumper,
      pageSizeOptions: paginationOptions.pageSizeOptions,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      onChange: (page, newPageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
          setPageSize(newPageSize);
        }

        // Update URL params
        const newParams = {
          ...params,
          page: page,
          pageSize: newPageSize
        };
        updateParams(newParams);
      },
      onShowSizeChange: (current, newPageSize) => {
        setCurrentPage(1);
        setPageSize(newPageSize);

        // Update URL params
        const newParams = {
          ...params,
          page: 1,
          pageSize: newPageSize
        };
        updateParams(newParams);
      }
    };
  }, [paginationOptions, currentPage, pageSize, total, params, updateParams]);

  // Update internal state when URL params change
  useEffect(() => {
    if (params.page) {
      setCurrentPage(parseInt(params.page));
    }
    if (params.pageSize) {
      setPageSize(parseInt(params.pageSize));
    }
  }, [params.page, params.pageSize]);

  // Enhanced columns with filtering and sorting
  const enhancedColumns = useMemo(() => {
    return columns.map(col => {
      const columnConfig = {
        ...col,
        key: col.dataIndex,
        sortDirections: ['descend', 'ascend'],
      };
      const filterConfig = filterOptions.find(f => f.field === col.dataIndex);
      if (filterConfig) {
        columnConfig.filters = filterConfig.options?.map(opt => ({
          text: opt.label,
          value: opt.value
        }));
        columnConfig.filterMultiple = filterConfig.type === FILTER_TYPES.MULTIPLE;
      }

      return columnConfig;
    });
  }, [columns, filterOptions]);

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (pagination, filters, sorter) => {
    const newParams = { ...params };

    // Update pagination
    newParams.page = pagination.current;
    newParams.pageSize = pagination.pageSize;

    // Update filters - using field names as keys
    Object.keys(filters).forEach(field => {
      if (filters[field] && filters[field].length > 0) {
        newParams[field] = filters[field];
      } else {
        delete newParams[field];
      }
    });

    // Update sorting
    if (sorter && sorter.field) {
      newParams.sortField = sorter.field;
      newParams.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      delete newParams.sortField;
      delete newParams.sortOrder;
    }

    // Update internal state
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    // Update URL params
    updateParams(newParams);
  };

  // Remove individual filter
  const removeFilter = (field) => {
    const newParams = { ...params };
    delete newParams[field];
    newParams.page = 1; // Reset to first page

    setCurrentPage(1);
    updateParams(newParams);
  };

  // Reset all filters
  const handleReset = () => {
    const resetParams = {
      page: 1,
      pageSize: paginationOptions.pageSize
    };

    setCurrentPage(1);
    setPageSize(paginationOptions.pageSize);
    updateParams(resetParams);
  };

  // Handle custom filter change
  const handleCustomFilterChange = (field, value, filterType) => {
    const newParams = { ...params };

    if (value) {
      if (filterType === FILTER_TYPES.MULTIPLE && value.length === 0) {
        delete newParams[field];
      } else {
        newParams[field] = value;
      }
    } else {
      delete newParams[field];
    }

    // Always reset to page 1 when filters change
    newParams.page = 1;
    setCurrentPage(1);
    updateParams(newParams);
  };

  // Render custom filter components
  const renderCustomFilters = () => {
    return filterOptions.map(filter => {
      const currentValue = params[filter.field];

      switch (filter.type) {
        case FILTER_TYPES.SINGLE:
          return (
            <Col key={filter.field} md={3} className="mb-3">
              <div className="form-group">
                {/* <label className='f-s-14'><strong>{filter.label}</strong></label> */}
                <Select
                  style={{ width: '100%' }}
                  placeholder={`Select ${filter.label}`}
                  value={currentValue || undefined}
                  onChange={(value) => handleCustomFilterChange(filter.field, value, filter.type)}
                  allowClear
                >
                  {filter.options?.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          );

        case FILTER_TYPES.MULTIPLE:
          return (
            <Col key={filter.field} md={3} className="mb-3">
              <div className="form-group">
                {/* <label className='f-s-14'><strong>{filter.label}</strong></label> */}
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={`Select ${filter.label}`}
                  value={currentValue || []}
                  onChange={(value) => handleCustomFilterChange(filter.field, value, filter.type)}
                  allowClear
                >
                  {filter.options?.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          );

        case FILTER_TYPES.DATE:
          return (
            <Col key={filter.field} md={3} className="mb-3">
              <div className="form-group">
                {/* <label className='f-s-14'><strong>{filter.label}</strong></label> */}
                <RangePicker
                  style={{ width: '100%' }}
                  value={currentValue && currentValue.length === 2 ? [moment(currentValue[0]), moment(currentValue[1])] : null}
                  onChange={(dates) => {
                    const value = dates && dates[0] && dates[1]
                      ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
                      : undefined;
                    handleCustomFilterChange(filter.field, value, filter.type);
                  }}
                />
              </div>
            </Col>
          );

        default:
          return null;
      }
    });
  };

  // Active filters display
  const renderActiveFilters = () => {
    const activeFilters = filterOptions.filter(filter => params[filter.field]);

    if (activeFilters.length === 0) return null;

    return (
      <div className="mb-3">
        <Space wrap>
          <strong className='f-s-12'>Active Filters:</strong>
          {activeFilters.map(filter => {
            const value = params[filter.field];
            let displayValue = value;

            if (Array.isArray(value)) {
              if (filter.type === FILTER_TYPES.DATE) {
                displayValue = value.join(' to ');
              } else {
                // For multiple select, show labels
                displayValue = value.map(v => {
                  const option = filter.options?.find(opt => opt.value === v);
                  return option ? option.label : v;
                }).join(', ');
              }
            } else if (filter.options) {
              // For single select, show label
              const option = filter.options.find(opt => opt.value === value);
              displayValue = option ? option.label : value;
            }

            return (
              <Tag
                key={filter.field}
                closable
                onClose={() => removeFilter(filter.field)}
              >
                {filter.label}: {displayValue}
              </Tag>
            );
          })}
          <Button type="link" size="small" onClick={handleReset}>
            Clear All
          </Button>
        </Space>
      </div>
    );
  };
  return (
    <div>
      {/* {filterOptions.length > 0 && (
        <Row>
          {renderCustomFilters()}
        </Row>
      )} */}

      {/* {renderActiveFilters()} */}
      <div>
        <Table
          columns={enhancedColumns}
          dataSource={data}
          rowKey={(record) => record.id || record.key || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          bordered={bordered}
          pagination={
            paginationConfig
          }
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};

export default AdvancedTable;